<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
    <!-- Header -->
    <header class="container mx-auto py-6 flex justify-between items-center">
      <div class="flex items-center space-x-2">
        <Code size="32" class="text-green-400" />
        <span class="text-2xl font-bold">Clear Start Solutions</span>
      </div>
      <nav>
        <ul class="flex space-x-6">
          <li><a href="#services" class="hover:text-green-400 transition-colors">Services</a></li>
          <li><a href="#about" class="hover:text-green-400 transition-colors">About</a></li>
          <li><router-link class="hover:text-green-400 transition-colors" to="contactus#contactusform">Contact Us</router-link></li>

        </ul>
      </nav>
    </header>

    <!-- Hero Section -->
    <section class="container mx-auto py-20 text-center">
      <h1 class="text-5xl font-bold mb-6">Empowering Digital Growth</h1>
      <p class="text-xl mb-8 max-w-2xl mx-auto">
        We specialize in WordPress and Node.js development to create powerful, scalable web solutions for your
        business.
      </p>
      <a href="#services" class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-500 hover:bg-green-600">
        Explore Our Services
        <ChevronRight class="ml-2" :size="20" />
      </a>
    </section>

    <!-- Services Section -->
    <section id="services" class="container mx-auto py-20">
      <h2 class="text-3xl font-bold mb-12 text-center">Our Specialized Services</h2>
      <div class="w-full max-w-3xl mx-auto">
        <div class="mb-6">
          <div class="grid w-full grid-cols-2">
            <button
                @click="activeTab = 'wordpress'"
                :class="['py-2 px-4 text-center', activeTab === 'wordpress' ? 'bg-white text-gray-900' : 'bg-gray-700 text-white']"
            >
              WordPress Development
            </button>
            <button
                @click="activeTab = 'nodejs'"
                :class="['py-2 px-4 text-center', activeTab === 'nodejs' ? 'bg-white text-gray-900' : 'bg-gray-700 text-white']"
            >
              Node.js Solutions
            </button>
          </div>
        </div>
        <div v-if="activeTab === 'wordpress'" class="bg-gray-800 rounded-lg p-6 space-y-4">
          <div class="flex items-center space-x-4">
            <Globe :size="48" class="text-green-400" />
            <div>
              <h3 class="text-xl font-semibold mb-2">Headless WordPress</h3>
              <p class="text-gray-300">
                We build decoupled WordPress solutions, combining the power of WordPress CMS with modern front-end
                technologies for optimal performance and flexibility.
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <Smartphone :size="48" class="text-green-400" />
            <div>
              <h3 class="text-xl font-semibold mb-2">WooCommerce Optimization</h3>
              <p class="text-gray-300">
                We optimize WooCommerce stores for speed and conversion, implementing custom features and
                integrations to boost your e-commerce success.
              </p>
            </div>
          </div>
        </div>
        <div v-if="activeTab === 'nodejs'" class="bg-gray-800 rounded-lg p-6 space-y-4">
          <div class="flex items-center space-x-4">
            <Code :size="48" class="text-green-400" />
            <div>
              <h3 class="text-xl font-semibold mb-2">Microservices Architecture</h3>
              <p class="text-gray-300">
                We design and implement scalable microservices using Node.js, enabling modular and maintainable
                backend systems for complex applications.
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <Users :size="48" class="text-green-400" />
            <div>
              <h3 class="text-xl font-semibold mb-2">Real-time Collaboration Tools</h3>
              <p class="text-gray-300">
                We develop real-time applications and collaboration tools using Node.js and WebSockets, perfect
                for chat systems, live dashboards, and interactive platforms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="container mx-auto py-20 text-center">
      <h2 class="text-3xl font-bold mb-6">About Clear Start Solutions</h2>
      <p class="text-xl mb-8 max-w-3xl mx-auto">
        With over 20 years of experience in web and mobile development, GC Group has been at the forefront of digital innovation. Our seasoned team of experts has successfully delivered cutting-edge solutions across various industries, combining deep technical knowledge with a passion for creating exceptional user experiences.
      </p>
      <div class="flex justify-center space-x-4">
        <div class="text-center">
          <h3 class="text-4xl font-bold text-green-400">20+</h3>
          <p class="text-lg">Years of Experience</p>
        </div>
        <div class="text-center">
          <h3 class="text-4xl font-bold text-green-400">500+</h3>
          <p class="text-lg">Projects Completed</p>
        </div>
        <div class="text-center">
          <h3 class="text-4xl font-bold text-green-400">100%</h3>
          <p class="text-lg">Client Satisfaction</p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section id="contact" class="bg-green-600 py-20">
      <div class="container mx-auto text-center">
        <h2 class="text-3xl font-bold mb-6">Ready to elevate your web presence?</h2>
        <p class="text-xl mb-8 max-w-2xl mx-auto">
          Let's discuss how our WordPress and Node.js expertise can drive your business forward.
        </p>
        <router-link to="contactus#contactusform" custom v-slot="{ navigate, href }">
          <button @click="navigate" :href="href" class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-green-600 bg-white hover:bg-gray-100">
            Get in Touch
            <Users class="ml-2" :size="20" />
          </button>
        </router-link>
      </div>
    </section>
    <router-view></router-view>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Code, Smartphone, Globe, Users, ChevronRight } from 'lucide-vue-next'

const activeTab = ref('wordpress')


</script>

<style scoped>
/* Add any additional component-specific styles here */
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}

.from-gray-900 {
  --tw-gradient-from: #111827;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(17, 24, 39, 0));
}

.to-gray-800 {
  --tw-gradient-to: #1f2937;
}

.text-green-400 {
  --tw-text-opacity: 1;
  color: rgba(52, 211, 153, var(--tw-text-opacity));
}

.bg-green-500 {
  --tw-bg-opacity: 1;
  background-color: rgba(16, 185, 129, var(--tw-bg-opacity));
}

.bg-green-600 {
  --tw-bg-opacity: 1;
  background-color: rgba(5, 150, 105, var(--tw-bg-opacity));
}

.hover\:bg-green-600:hover {
  --tw-bg-opacity: 1;
  background-color: rgba(5, 150, 105, var(--tw-bg-opacity));
}

.hover\:bg-gray-100:hover {
  --tw-bg-opacity: 1;
  background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
}

/* Add any additional custom styles here */
</style>
